import { useEffect, useState } from 'react';
import { useLocation, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import { defaultSiteData } from './data/siteData';
import Home from './pages/Home';
import About from './pages/About';
import AboutAreasOfFocus from './pages/AboutAreasOfFocus';
import AboutStrategicPillars from './pages/AboutStrategicPillars';
import AboutGovernance from './pages/AboutGovernance';
import Projects from './pages/Projects';
import UnitedNations from './pages/UnitedNations';
import UnUniversity from './pages/UnUniversity';
import UnHumanRights from './pages/UnHumanRights';
import SdgInteractive from './pages/SdgInteractive';
import AugpUsa from './pages/AugpUsa';
import Contact from './pages/Contact';

const STORAGE_KEY = 'ungdo-site-data-v8';
const ADMIN_AUTH_KEY = 'ungdo-admin-auth';

function App() {
  const location = useLocation();

  const [siteData, setSiteData] = useState(() => {
    try {
      localStorage.removeItem('ungdo-site-data');
      localStorage.removeItem('ungdo-site-data-v2');
      localStorage.removeItem('ungdo-site-data-v3');
      localStorage.removeItem('ungdo-site-data-v4');
      localStorage.removeItem('ungdo-site-data-v5');
      localStorage.removeItem('ungdo-site-data-v6');
      localStorage.removeItem('ungdo-site-data-v7');
    } catch (e) {}

    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultSiteData;

    try {
      const parsed = JSON.parse(saved);
      if (!parsed || typeof parsed !== 'object') return defaultSiteData;

      return {
        ...defaultSiteData,
        ...parsed,
        bannerSlides: defaultSiteData.bannerSlides,
        leadership: Array.isArray(parsed.leadership) && parsed.leadership.length > 0 ? parsed.leadership : defaultSiteData.leadership,
        internationalGovernors: Array.isArray(parsed.internationalGovernors) && parsed.internationalGovernors.length > 0 ? parsed.internationalGovernors : defaultSiteData.internationalGovernors,
        advisors: Array.isArray(parsed.advisors) && parsed.advisors.length > 0 ? parsed.advisors : defaultSiteData.advisors,
        projects: Array.isArray(parsed.projects) ? parsed.projects : defaultSiteData.projects,
        gallery: Array.isArray(parsed.gallery) ? parsed.gallery : defaultSiteData.gallery,
        focusAreas: Array.isArray(parsed.focusAreas) ? parsed.focusAreas : defaultSiteData.focusAreas,
        stats: Array.isArray(parsed.stats) ? parsed.stats : defaultSiteData.stats
      };
    } catch (e) {
      console.error("Failed to parse saved siteData from localStorage:", e);
      return defaultSiteData;
    }
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem(ADMIN_AUTH_KEY) === 'true');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(siteData));
  }, [siteData]);

  // Update Document Title dynamically for search engine optimization (SEO)
  useEffect(() => {
    const path = location.pathname;
    if (path === '/about') {
      document.title = 'About UNGDO - Overview & Mission | Dr. Tharindu Wijenayake';
    } else if (path === '/about/areas-of-focus') {
      document.title = '12 Core Areas of Focus | UNGDO Sustainable Development';
    } else if (path === '/about/strategic-pillars') {
      document.title = 'Strategic Pillars & SDG Commitment | UNGDO';
    } else if (path === '/about/governance') {
      document.title = 'Governance & Executive Leadership | UNGDO Board of Directors';
    } else if (path === '/projects') {
      document.title = 'Featured Projects & Infrastructural Funding | UNGDO';
    } else if (path === '/united-nations') {
      document.title = 'United Nations Initiatives | UNGDO';
    } else if (path === '/united-nations/sdg-interactive') {
      document.title = 'SDG Interactive - Sustainable Development Goals | UNGDO';
    } else if (path === '/united-nations/un-human-rights-declaration') {
      document.title = 'UN Human Rights Declaration | UNGDO';
    } else if (path === '/united-nations/un-university-for-global-peace') {
      document.title = 'UN University for Global Peace | UNGDO';
    } else if (path === '/augp-usa') {
      document.title = 'AUGP USA Headquarters | UNGDO';
    } else if (path === '/contact') {
      document.title = 'Contact Head Office | UNGDO';
    } else {
      document.title = 'United Nations Global Development Organization (UNGDO)';
    }
  }, [location.pathname]);

  return (
    <Layout siteData={siteData}>
      <Routes>
        <Route path="/" element={<Home siteData={siteData} />} />
        <Route path="/about" element={<About siteData={siteData} />} />
        <Route path="/about/areas-of-focus" element={<AboutAreasOfFocus siteData={siteData} />} />
        <Route path="/about/strategic-pillars" element={<AboutStrategicPillars siteData={siteData} />} />
        <Route path="/about/governance" element={<AboutGovernance siteData={siteData} />} />
        <Route path="/projects" element={<Projects siteData={siteData} />} />
        <Route path="/united-nations" element={<UnitedNations siteData={siteData} />} />
        <Route path="/united-nations/un-university-for-global-peace" element={<UnUniversity siteData={siteData} />} />
        <Route path="/united-nations/un-human-rights-declaration" element={<UnHumanRights siteData={siteData} />} />
        <Route path="/united-nations/sdg-interactive" element={<SdgInteractive siteData={siteData} />} />
        <Route path="/augp-usa" element={<AugpUsa siteData={siteData} />} />
        <Route path="/contact" element={<Contact siteData={siteData} />} />
        <Route
          path="/admin"
          element={
            isAuthenticated ? (
              <AdminPanel siteData={siteData} setSiteData={setSiteData} onLogout={() => {
                localStorage.removeItem(ADMIN_AUTH_KEY);
                setIsAuthenticated(false);
              }} />
            ) : (
              <AdminLogin onLogin={() => setIsAuthenticated(true)} />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
